import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ChevronLeft, Sparkles, Wand2, ArrowRight, Loader2, RotateCcw } from "lucide-react";
import { API, LOGO_URL, imgUrl } from "@/config";
import { Fireflies } from "@/components/effects/Fireflies";

const AGE_OPTS = [
  [3, "0–6 months"], [9, "6–12 months"], [12, "1 year"], [24, "2 years"],
  [36, "3 years"], [48, "4 years"], [60, "5 years"], [72, "6–7 years"], [96, "8+ years"],
];
const CHOICE_STEPS = [
  { key: "weight_pref", title: "How light do you want the stroller itself to be?", opts: [["ultra_light", "Ultra lightweight"], ["light", "Lightweight"], ["medium", "Medium weight"], ["heavy_duty", "Heavy-duty"], ["none", "No preference"]] },
  { key: "fold_importance", title: "How important is an easy fold?", opts: [["very", "Very important"], ["somewhat", "Somewhat important"], ["none", "Fold doesn't matter"]] },
  { key: "main_use", title: "Where will you mainly use the stroller?", opts: [["disney", "Disney World / theme parks"], ["universal", "Universal"], ["resort", "Resorts"], ["mixed", "Mixed use"]] },
  { key: "priorities", title: "Pick your top priorities", multi: true, opts: [["comfort", "Child comfort"], ["lightweight", "Lightweight stroller"], ["easy_fold", "Easy fold"], ["compact_fold", "Compact fold"], ["storage", "Large storage basket"], ["smooth_ride", "Smooth ride"], ["maneuverability", "Maneuverability"], ["canopy", "Large canopy"], ["nap", "Nap friendliness"], ["all_terrain", "All-terrain ability"]] },
  { key: "recline_pref", title: "How much recline do you want?", opts: [["full", "Near-flat / full recline"], ["deep", "Deep recline"], ["partial", "Partial recline"], ["none", "Doesn't matter"]] },
  { key: "napping", title: "Will your child likely nap in the stroller?", opts: [["yes", "Yes"], ["no", "No"], ["maybe", "Maybe"]] },
  { key: "canopy_important", title: "Is a large canopy important?", opts: [["yes", "Yes"], ["none", "No preference"]] },
  { key: "storage_important", title: "Is a large storage basket important?", opts: [["yes", "Yes"], ["none", "No preference"]] },
  { key: "terrain", title: "What terrain will you mainly roll on?", opts: [["smooth", "Smooth pavement"], ["theme_parks", "Theme parks"], ["mixed", "Mixed pavement"], ["rough", "Rough pavement"], ["all_terrain", "All-terrain"]] },
  { key: "stroller_count_pref", title: "Would you rather push one stroller or split across multiple?", onlyIfDouble: true, opts: [["one", "One stroller for everyone"], ["multiple", "Multiple strollers is fine"], ["none", "No preference"]] },
  { key: "double_style", title: "For a double stroller, which style do you prefer?", onlyIfDouble: true, opts: [["side_by_side", "Side-by-side"], ["tandem", "Tandem"], ["none", "No preference"]] },
];

const OptBtn = ({ active, onClick, children, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={`w-full text-left rounded-xl border px-4 py-3 text-sm transition-all ${active ? "border-[#FFD166] bg-[#FFD166]/12 text-[#FFD166] shadow-[0_0_18px_rgba(255,209,102,0.15)]" : "border-[#FFD166]/20 text-[#F6F1E4]/90 hover:border-[#FFD166]/55 hover:bg-white/5"}`}>
    {children}
  </button>
);

export default function StrollerQuiz() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [numChildren, setNumChildren] = useState(null);
  const [children, setChildren] = useState([]);
  const [answers, setAnswers] = useState({ priorities: [] });
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const choiceSteps = useMemo(
    () => CHOICE_STEPS.filter((s) => !s.onlyIfDouble || (numChildren || 1) >= 2),
    [numChildren]
  );
  // step 0 = num children, step 1 = child details, then choice steps
  const totalSteps = 2 + choiceSteps.length;
  const progress = Math.min(100, Math.round((step / totalSteps) * 100));

  const submit = async (finalAnswers) => {
    setLoading(true); setError(null);
    try {
      const payload = { num_children: numChildren, children, ...finalAnswers };
      const r = await axios.post(`${API}/stroller-quiz`, payload);
      setResults(r.data.results || []);
    } catch (e) {
      setError(e.response?.data?.detail || "Couldn't calculate your matches — please try again.");
    } finally { setLoading(false); }
  };

  const pick = (s, val) => {
    let next;
    if (s.multi) {
      const cur = answers[s.key] || [];
      next = { ...answers, [s.key]: cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val] };
      setAnswers(next);
      return; // multi steps advance via Next button
    }
    next = { ...answers, [s.key]: val };
    setAnswers(next);
    const idx = choiceSteps.indexOf(s);
    if (idx === choiceSteps.length - 1) submit(next);
    else setStep(step + 1);
  };

  const restart = () => { setStep(0); setNumChildren(null); setChildren([]); setAnswers({ priorities: [] }); setResults(null); setError(null); };

  const childrenComplete = children.length === (numChildren || 0) && children.every((c) => c.age_months && Number(c.weight_lbs) > 0);

  return (
    <div className="min-h-screen bg-[#04090F] relative overflow-hidden no-custom-cursor">
      <Fireflies count={14} />
      <div className="relative z-10">
        <header className="border-b border-[#FFD166]/15 bg-[#0A1A26]/60 backdrop-blur">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-[#FFD166] hover:text-[#F4A93A] text-sm" data-testid="quiz-back-link">
              <ChevronLeft className="w-4 h-4" /> Back to catalog
            </Link>
            <img src={LOGO_URL} alt="Enchanted Gear Rentals" className="h-10" style={{ filter: "url(#knockoutBlack)" }} />
            <div className="w-24" />
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8 lg:py-12">
          {!results && (
            <>
              <p className="eyebrow mb-2 flex items-center gap-2"><Wand2 className="w-3.5 h-3.5" /> Stroller matchmaker</p>
              <h1 className="font-serif text-3xl sm:text-4xl text-[#F6F1E4] mb-6">Which stroller should I get?</h1>
              <div className="h-1.5 rounded-full bg-white/10 mb-8 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#D97B37] to-[#FFD166] transition-all duration-500" style={{ width: `${progress}%` }} data-testid="quiz-progress" />
              </div>
            </>
          )}

          {loading && (
            <div className="glass p-10 text-center" data-testid="quiz-loading">
              <Loader2 className="w-7 h-7 text-[#FFD166] animate-spin mx-auto mb-4" />
              <p className="font-serif text-2xl text-[#F6F1E4]">Matching strollers to your family…</p>
            </div>
          )}

          {error && !loading && (
            <div className="glass p-8 text-center" data-testid="quiz-error">
              <p className="text-[#F4A93A] mb-4">{error}</p>
              <button onClick={restart} className="btn-primary">Start over</button>
            </div>
          )}

          {/* Step 0 — number of children */}
          {!loading && !results && !error && step === 0 && (
            <div className="glass p-6 sm:p-8" data-testid="quiz-step-children">
              <h2 className="font-serif text-xl sm:text-2xl text-[#F6F1E4] mb-5">How many children need a stroller?</h2>
              <div className="grid grid-cols-4 gap-3">
                {[1, 2, 3, 4].map((n) => (
                  <button key={n} type="button" data-testid={`quiz-children-${n}`}
                    onClick={() => { setNumChildren(n); setChildren(Array.from({ length: n }, (_, i) => children[i] || {})); setStep(1); }}
                    className={`rounded-xl border py-5 font-serif text-2xl transition-all ${numChildren === n ? "border-[#FFD166] bg-[#FFD166]/12 text-[#FFD166]" : "border-[#FFD166]/20 text-[#F6F1E4] hover:border-[#FFD166]/55"}`}>
                    {n}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 1 — ages & weights */}
          {!loading && !results && !error && step === 1 && (
            <div className="glass p-6 sm:p-8" data-testid="quiz-step-details">
              <h2 className="font-serif text-xl sm:text-2xl text-[#F6F1E4] mb-1">Tell us about your little one{(numChildren || 1) > 1 ? "s" : ""}</h2>
              <p className="text-xs text-[#B9C8D5] mb-5">We use this to rule out strollers that aren't a safe fit.</p>
              <div className="space-y-4">
                {Array.from({ length: numChildren || 1 }).map((_, i) => (
                  <div key={i} className="rounded-xl border border-[#FFD166]/20 bg-black/25 p-4" data-testid={`quiz-child-${i}`}>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#FFD166] mb-2">Child {i + 1}</div>
                    <div className="grid grid-cols-2 gap-3">
                      <label className="text-xs text-[#B9C8D5]">Age
                        <select value={children[i]?.age_months || ""} data-testid={`quiz-child-${i}-age`}
                          onChange={(e) => setChildren((c) => c.map((x, j) => j === i ? { ...x, age_months: Number(e.target.value) } : x))}
                          className="mt-1 w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg text-sm">
                          <option value="">Choose…</option>
                          {AGE_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                      </label>
                      <label className="text-xs text-[#B9C8D5]">Weight (lbs)
                        <input type="number" inputMode="decimal" min="1" max="200" step="1" placeholder="e.g. 32"
                          value={children[i]?.weight_lbs ?? ""} data-testid={`quiz-child-${i}-weight`}
                          onChange={(e) => setChildren((c) => c.map((x, j) => j === i ? { ...x, weight_lbs: e.target.value === "" ? "" : Number(e.target.value) } : x))}
                          className="mt-1 w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg text-sm placeholder:text-[#B9C8D5]/40" />
                      </label>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex justify-between">
                <button type="button" onClick={() => setStep(0)} className="btn-ghost" data-testid="quiz-back-btn">Back</button>
                <button type="button" onClick={() => setStep(2)} disabled={!childrenComplete} className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed" data-testid="quiz-next-btn">
                  Next <ArrowRight className="w-4 h-4 inline ml-1" />
                </button>
              </div>
            </div>
          )}

          {/* Choice steps */}
          {!loading && !results && !error && step >= 2 && step < totalSteps && (() => {
            const s = choiceSteps[step - 2];
            if (!s) return null;
            const isLast = choiceSteps.indexOf(s) === choiceSteps.length - 1;
            return (
              <div className="glass p-6 sm:p-8" data-testid={`quiz-step-${s.key}`}>
                <h2 className="font-serif text-xl sm:text-2xl text-[#F6F1E4] mb-5">{s.title}</h2>
                {s.multi && <p className="text-xs text-[#B9C8D5] -mt-3 mb-4">Tap all that matter to you.</p>}
                <div className={`grid gap-2.5 ${s.opts.length > 5 ? "sm:grid-cols-2" : ""}`}>
                  {s.opts.map(([v, l]) => (
                    <OptBtn key={v} testId={`quiz-opt-${s.key}-${v}`}
                      active={s.multi ? (answers[s.key] || []).includes(v) : answers[s.key] === v}
                      onClick={() => pick(s, v)}>
                      {l}
                    </OptBtn>
                  ))}
                </div>
                <div className="mt-6 flex justify-between">
                  <button type="button" onClick={() => setStep(step - 1)} className="btn-ghost" data-testid="quiz-back-btn">Back</button>
                  {s.multi && (
                    <button type="button" data-testid="quiz-next-btn"
                      onClick={() => (isLast ? submit(answers) : setStep(step + 1))}
                      className="btn-primary">
                      {isLast ? "See my matches" : "Next"} <ArrowRight className="w-4 h-4 inline ml-1" />
                    </button>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Results */}
          {results && !loading && (
            <div data-testid="quiz-results">
              <p className="eyebrow mb-2 flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" /> Your matches</p>
              <h1 className="font-serif text-3xl sm:text-4xl text-[#F6F1E4] mb-6">
                {results.length ? "Here's what we'd pick for your family" : "Hmm — no safe match found"}
              </h1>
              {results.length === 0 && (
                <div className="glass p-8 text-center">
                  <p className="text-sm text-[#B9C8D5] mb-4">Based on your children's ages and weights, none of our current strollers are a safe fit. Message us — we'll help you find an option.</p>
                  <button onClick={restart} className="btn-primary" data-testid="quiz-restart">Try different answers</button>
                </div>
              )}
              <div className="space-y-4">
                {results.map((r, i) => {
                  const items = r.items || [{ product_id: r.product_id, name: r.name, image: r.image, tier_1_3: r.tier_1_3, tier_8_plus: r.tier_8_plus, qty: r.quantity || 1 }];
                  const unitCount = items.reduce((s, it) => s + (it.qty || 1), 0);
                  const multi = unitCount > 1;
                  const perDay = items.reduce((s, it) => s + Math.floor(it.tier_8_plus ?? it.tier_1_3 ?? 0) * (it.qty || 1), 0);
                  const title = items.map((it) => ((it.qty || 1) > 1 ? `${it.qty} × ${it.name}` : it.name)).join(" + ");
                  return (
                    <div key={`${r.product_id}-${i}`} className={`glass p-4 sm:p-5 flex gap-4 items-start ${i === 0 ? "border-[#FFD166]/60 shadow-[0_0_30px_rgba(255,209,102,0.12)]" : ""}`} data-testid={`quiz-result-${i}`}>
                      <div className="shrink-0 flex flex-col gap-1.5">
                        {items.map((it, k) => (
                          <img key={it.product_id || it.id || it.name} src={imgUrl(it.image)} alt={it.name}
                            className={`${items.length > 1 ? "w-20 h-20 sm:w-24 sm:h-24" : "w-24 h-24 sm:w-28 sm:h-28"} rounded-xl object-contain bg-black/30`} />
                        ))}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full font-semibold ${i === 0 ? "bg-[#FFD166] text-[#040A0F]" : "bg-white/10 text-[#B9C8D5]"}`}>{r.rank_label}</span>
                          {multi && (
                            <span className="text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full font-semibold bg-[#F4A93A]/20 border border-[#F4A93A]/50 text-[#F4A93A]" data-testid={`quiz-combo-badge-${i}`}>
                              {r.combo_label || "Combo"}
                            </span>
                          )}
                          <span className="text-emerald-300 font-serif text-lg" data-testid={`quiz-match-pct-${i}`}>{r.match_pct}% match</span>
                        </div>
                        <div className="font-serif text-xl sm:text-2xl text-[#F6F1E4] mt-1">{title}</div>
                        <p className="text-[13px] text-[#B9C8D5] mt-1 leading-snug">{r.reason}</p>
                        <div className="mt-3 flex items-center gap-3 flex-wrap">
                          {items.map((it) => (
                            <button key={it.product_id} type="button" onClick={() => nav(`/?product=${it.product_id}`)} className="btn-primary !py-2 !px-4 text-sm" data-testid={`quiz-view-${it.product_id}`}>
                              {items.length > 1 ? `View ${it.name}` : "View this stroller"}
                            </button>
                          ))}
                          <span className="text-xs text-[#FFD166]">
                            from ${perDay}/day{multi ? " for all" : ""}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {results.length > 0 && (
                <button onClick={restart} className="mt-6 text-sm text-[#B9C8D5] hover:text-[#FFD166] inline-flex items-center gap-1.5" data-testid="quiz-restart">
                  <RotateCcw className="w-3.5 h-3.5" /> Retake the quiz
                </button>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
